import {
	DocumentManager,
	RestWebService,
	WebServiceFactory,
	WebserviceOperationType,
	WebServiceType,
	WebServiceTypes
} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocument from "../document/PortalDocument";
import PortalDocumentManager from "../document/PortalDocumentManager";
import Exception from "../../shared/exception/Exception";
import ContentMode from "../ContentMode";
import ErrorCode from "../../shared/exception/ErrorCode";
import Message from "../../shared/log/Message";
import {MessageType} from "../../shared/log/MessageInterface";
import AbstractSession from "../../shared/session/AbstractSession";
import PortalContext from "../PortalContext";
import {Administration} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import {
	KeyStorePassword,
	LoginOptions,
	Settings,
	UserCertificates
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import UserSettings from "./UserSettings";
import Immutable from "../../shared/immutable/Immutable";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {Internal} from "../../shared/models/Internal";
import {PortalConverterWebService} from "./PortalConverterWebService";

export default class PortalSession extends AbstractSession<PortalContext, PortalSession> {
	private userSettings: UserSettings;

	constructor(url: URL, context: PortalContext, tlsContext?: any) {
		super(url, context, tlsContext);

		this.userSettings = new UserSettings();
	}

	public async initialize(): Promise<void> {
		this.getDocumentManager().context = this.context;
		await super.initialize();
	}

	/**
	 * Login into the server with user and password
	 */
	public async login(options?: LoginOptions, username?: string, password?: string): Promise<void> {
		await super.login(options, username, password);

		this.context.getPage().setState({
			contentMode: ContentMode.STARTSCREEN,
			context: this.context
		});
	}

	public getDocumentManager(): PortalDocumentManager {
		return super.getDocumentManager() as PortalDocumentManager;
	}

	private getWebService(webServiceType: WebServiceType): RestWebService<PortalDocument, any> {
		switch (webServiceType) {
			case WebServiceTypes.CONVERTER:
				return new PortalConverterWebService(this);
			case WebServiceTypes.TOOLBOX:
				return WebServiceFactory.createInstance(this, WebServiceTypes.TOOLBOX);
			case WebServiceTypes.URLCONVERTER:
				return WebServiceFactory.createInstance(this, WebServiceTypes.URLCONVERTER);
			case WebServiceTypes.BARCODE:
				return WebServiceFactory.createInstance(this, WebServiceTypes.BARCODE);
			case WebServiceTypes.OCR:
				return WebServiceFactory.createInstance(this, WebServiceTypes.OCR);
			case WebServiceTypes.PDFA:
				return WebServiceFactory.createInstance(this, WebServiceTypes.PDFA);
			case WebServiceTypes.SIGNATURE:
				return WebServiceFactory.createInstance(this, WebServiceTypes.SIGNATURE);
		}

		throw new Exception("undefined Webservice");
	}

	public async executeRequest(options: {
		webServiceType: WebServiceType,
		operation: WebserviceOperationType,
		document?: PortalDocument,
		settings?: Settings,
		filename?: string,
		internal?: Internal,
		showMessage?: boolean
	}): Promise<void> {
		let webService: RestWebService<PortalDocument, any> = this.getWebService(options.webServiceType);
		let additionalParameter: URLSearchParams = webService.getAdditionalParameter();
		webService.setOperation(options.operation);
		webService.setSettings(FunctionHelper.applyValue(options.settings, new Settings({})));

		if (FunctionHelper.exists(options.internal) && options.webServiceType === WebServiceTypes.CONVERTER) {
			(webService as PortalConverterWebService<PortalDocument>).setInternal(options.internal);
		}

		if (FunctionHelper.exists(options.filename)) {
			additionalParameter.set("name", encodeURI(options.filename!));
		}

		if (FunctionHelper.exists(options.document)) {
			webService.setDocument(options.document);
			options.document?.setExecutionError();
		}

		try {
			await webService.process();
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			portalException.documentId = options.document?.getSourceDocumentId();
			throw await this.handleException(portalException, options.showMessage);
		}
	}

	public async handleException(exception: Error, showMessage?: boolean): Promise<Exception> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.reconnectServer();
			return portalException;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_INVALID_DOCUMENT_ID:
				window.location.reload();
				break;
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				this.clearSessionToken();
				this.context.getPage().setState({
					contentMode: ContentMode.STARTSCREEN,
					showLoginDialog: true,
					loginParameter: undefined
				});
				break;
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.refresh();

					this.context.getPage().setState({
						contentMode: ContentMode.STARTSCREEN
					});

					// no need to show timeout messages
					return portalException;
				} catch (exception: any) {
					return await this.handleException(exception, showMessage);
				}
			case ErrorCode.PDF_PASSWORD_OWNER:
				this.context.getPage().setState({
					showPasswordDialog: true
				});
				break;
		}

		let message: Message = new Message(portalException.message, MessageType.DANGER);
		message.code = portalException.code;
		message.visible = !FunctionHelper.exists(showMessage) ? true : showMessage!;

		if (FunctionHelper.exists(portalException.documentId)) {
			message.context = {
				filename: '"' + this.getDocumentManager().findDocument(portalException.documentId!).getFileName() + '" '
			};
		}

		this.context.getPage().addMessage(message);

		return portalException;
	}

	/**
	 * Creates new {@link PortalDocumentManager} instances
	 */
	protected createDocumentManager(): DocumentManager<PortalDocument> {
		return new PortalDocumentManager(this);
	}

	protected createAdministration(): Administration {
		// not needed in Portal
		return {} as Administration;
	}

	public async fetchCertificates(): Promise<UserCertificates | undefined> {
		let certificates = await super.fetchCertificates();
		this.context.getPage().setState({userCertificates: Immutable.from(certificates?.toJson())});
		return certificates;
	}

	public async updateCertificates(
		keystoreName: string, parameter: KeyStorePassword
	): Promise<UserCertificates | undefined> {
		let certificates = await super.updateCertificates(keystoreName, parameter);
		this.context.getPage().setState({userCertificates: Immutable.from(certificates?.toJson())});
		return certificates;
	}

	public updateUserSettings(settings?: UserSettings): void {
		this.userSettings = FunctionHelper.applyValue(settings?.clone(), new UserSettings());
		this.context.getPage().setState({userSettings: Immutable.from(this.userSettings.toJson())});
	}
}